import { createContext, useContext, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "../api/client";
import { resolveContext, UnknownContext, type EventContext } from "./event";

/**
 * THE EVENT, MADE AVAILABLE TO THE SCREENS.
 *
 * Three states, and each one is a screen rather than a blank page:
 *   — NO TOKEN at all: an error. The site has no meaning without an event; a
 *     bare address is a truncated link, and saying so is more use than showing a
 *     demonstration nobody asked for and that saves nowhere.
 *   — A TOKEN THAT RESOLVES: the real event.
 *   — A TOKEN THAT DOES NOT: said plainly. A participant who follows an expired
 *     link deserves to know that is what happened, not a spinner forever.
 *
 * Displayed strings are French: this is a French product.
 */

type EventState =
  | { kind: "missing" }
  | { kind: "loading"; token: string }
  | { kind: "ready"; event: EventContext }
  | { kind: "unknown"; token: string }
  | { kind: "unavailable"; token: string };

const Context = createContext<EventState>({ kind: "missing" });

export function EventProvider({ children }: { children: ReactNode }) {
  const { context } = useParams();

  const query = useQuery({
    queryKey: ["context", context],
    queryFn: ({ signal }) => resolveContext(context!, signal),
    enabled: !!context,
    // An unknown event stays unknown however many times we ask; retrying only
    // makes a participant wait longer for the same answer.
    retry: (count, error) =>
      !(error instanceof UnknownContext) &&
      !(error instanceof ApiError && error.status === 404) &&
      count < 2,
    staleTime: Infinity,
  });

  let state: EventState = { kind: "missing" };
  if (context) {
    if (query.data) state = { kind: "ready", event: query.data };
    else if (query.isPending) state = { kind: "loading", token: context };
    else if (
      query.error instanceof UnknownContext ||
      (query.error instanceof ApiError && query.error.status === 404)
    )
      state = { kind: "unknown", token: context };
    else state = { kind: "unavailable", token: context };
  }

  return <Context.Provider value={state}>{children}</Context.Provider>;
}

export function useEventState(): EventState {
  return useContext(Context);
}

/** The event, or `null` while it is missing or unresolved. */
export function useEvent(): EventContext | null {
  const state = useEventState();
  return state.kind === "ready" ? state.event : null;
}
